<div class="legal-shell">

  <header class="legal-title">
    <span class="ls-eyebrow">Vie privée · RGPD + Loi haïtienne 2017</span>
    <h1>{{ 'PRIVACY.TITLE' | translate }}</h1>
    <div class="meta">
      <span>Version <b>3.1</b></span>
      <span class="dot"></span>
      <span>En vigueur depuis le <b>1<sup>er</sup> mai 2026</b></span>
      <span class="dot"></span>
      <a href="#">Historique des versions</a>
    </div>
    <p class="lede">
      Ta vie privée est <b>au cœur</b> de notre projet. LyricStream est un service de prière et de louange —
      pas une plateforme publicitaire. Nous ne vendons pas tes données, nous ne traçons pas tes lectures
      hors de ton compte, nous ne profilons pas tes croyances pour quoi que ce soit. Voilà comment, en détail.
    </p>
  </header>

  <aside class="toc">
    <h2>{{ 'CONDITIONS.SOMMAIRE' | translate }}</h2>
    <ol>
      @for (section of sections; track section.id) {
        <li [class.on]="activeSection() === section.id">
          <a [routerLink]="['.']" [fragment]="section.id" (click)="goTo(section.id, $event)">{{ section.label }}</a>
        </li>
      }
    </ol>
  </aside>

  <main class="legal-body">

    <h2 id="p1"><span class="num">Section 1</span>Ce que nous collectons</h2>
    <p>Nous collectons uniquement ce qui est <b>strictement nécessaire</b> au bon fonctionnement du service.</p>

    <h3>Quand tu crées un compte</h3>
    <ul>
      <li><b>Adresse e-mail</b> — pour la connexion et les communications importantes.</li>
      <li><b>Prénom et nom</b> — affichés sur ton profil et tes contributions.</li>
      <li><b>Mot de passe</b> (si tu en choisis un) — stocké <b>haché</b> avec bcrypt, jamais en clair.</li>
      <li><b>Pays / fuseau horaire</b> — déduit de ton navigateur pour adapter les horaires affichés.</li>
    </ul>

    <h3>Quand tu utilises le service</h3>
    <ul>
      <li><b>Tes favoris, playlists, historique d'écoute</b> — pour te les afficher dans ta bibliothèque perso.</li>
      <li><b>Tes contributions</b> — paroles, accords, audio, traductions soumis par toi.</li>
      <li><b>Logs techniques basiques</b> — adresse IP, navigateur, pages visitées (conservés 30 jours pour la sécurité, anonymisés ensuite).</li>
    </ul>

    <h3>Quand tu paies (Premium)</h3>
    <ul>
      <li><b>Données de paiement</b> — traitées exclusivement par Stripe, nous ne stockons aucune information de carte bancaire.</li>
      <li><b>Adresse de facturation</b> — pour respecter les obligations comptables.</li>
    </ul>

    <h2 id="p2"><span class="num">Section 2</span>Pourquoi nous le collectons</h2>
    <p>Chaque donnée a un usage précis, jamais "au cas où"&nbsp;:</p>
    <table>
      <thead>
        <tr><th>Donnée</th><th>Finalité</th><th>Base légale</th></tr>
      </thead>
      <tbody>
        <tr><td>E-mail, mot de passe</td><td>Authentification</td><td>Contrat</td></tr>
        <tr><td>Favoris, playlists, historique</td><td>Service essentiel</td><td>Contrat</td></tr>
        <tr><td>Contributions</td><td>Mission éditoriale</td><td>Contrat + intérêt légitime</td></tr>
        <tr><td>Logs IP, navigateur</td><td>Sécurité, anti-abus</td><td>Intérêt légitime</td></tr>
        <tr><td>Préférences de communication</td><td>Newsletter, alertes</td><td>Consentement</td></tr>
        <tr><td>Données de paiement (Stripe)</td><td>Facturation Premium</td><td>Contrat + obligation légale</td></tr>
      </tbody>
    </table>

    <h2 id="p3"><span class="num">Section 3</span>Ce que nous ne faisons jamais</h2>
    <div class="callout">
      <b>Notre engagement absolu :</b>
      <ul>
        <li>Nous ne <b>vendons jamais</b> tes données à des tiers.</li>
        <li>Nous ne <b>traçons jamais</b> tes lectures pour de la publicité comportementale.</li>
        <li>Nous ne <b>profilons jamais</b> tes croyances religieuses ou ton appartenance dénominationnelle.</li>
        <li>Nous n'<b>analysons jamais</b> le contenu de tes playlists privées pour en tirer des inférences.</li>
        <li>Nous n'<b>installons aucun cookie publicitaire</b> ni pixel publicitaire (Meta Pixel, etc.), et aucun outil de statistiques à cookies sans ton accord.</li>
      </ul>
    </div>

    <h2 id="p4"><span class="num">Section 4</span>Cookies et traceurs</h2>
    <p>Par défaut, nous n'utilisons que <b>des cookies fonctionnels essentiels</b>&nbsp;:</p>
    <ul>
      <li><code>ls_session</code> — maintient ta session ouverte (HTTP-only, secure, 30 jours).</li>
      <li><code>ls_theme</code> — mémorise ton choix clair/sombre (localStorage, 1 an).</li>
      <li><code>ls_consent</code> — mémorise tes choix d'opt-in newsletter (12 mois).</li>
      <li><code>ls_analytics_consent</code> — mémorise ton choix « Accepter / Refuser » pour les statistiques (localStorage).</li>
    </ul>
    <p><b>Statistiques de visite.</b> Nous mesurons la fréquentation avec <b>Cloudflare Web Analytics</b>, qui ne dépose aucun cookie et ne te suit pas d'un site à l'autre.</p>
    <p><b>Statistiques détaillées (avec ton accord).</b> Si tu cliques « Accepter » dans le bandeau, nous chargeons <b>Google Analytics</b>, qui dépose les cookies <code>_ga</code> et <code>_ga_&lt;ID&gt;</code> (13 mois) pour comprendre quelles pages et fonctionnalités sont utilisées. Si tu refuses, Google Analytics n'est jamais chargé et ton accès au site reste identique. Tu peux changer d'avis à tout moment avec le lien « Gérer les cookies » en bas de page.</p>

    <h2 id="p5"><span class="num">Section 5</span>Durée de conservation</h2>
    <table>
      <thead>
        <tr><th>Type</th><th>Durée</th></tr>
      </thead>
      <tbody>
        <tr><td>Compte actif (e-mail, profil)</td><td>Tant que le compte existe</td></tr>
        <tr><td>Compte inactif &gt; 24 mois</td><td>Anonymisation puis suppression</td></tr>
        <tr><td>Logs de connexion / IP</td><td>30 jours, puis anonymisés</td></tr>
        <tr><td>Contributions publiées</td><td>Tant que la bibliothèque existe</td></tr>
        <tr><td>Factures Premium</td><td>10 ans (obligation comptable)</td></tr>
        <tr><td>Données après suppression</td><td>30 jours en backup, puis purgées</td></tr>
      </tbody>
    </table>

    <h2 id="p6"><span class="num">Section 6</span>Partage avec des tiers</h2>
    <p>Nous travaillons avec un nombre minimal de sous-traitants, tous certifiés conformes RGPD&nbsp;:</p>
    <table>
      <thead>
        <tr><th>Partenaire</th><th>Rôle</th><th>Localisation</th></tr>
      </thead>
      <tbody>
        <tr><td>Stripe</td><td>Paiements Premium</td><td>UE / USA (clauses contractuelles)</td></tr>
        <tr><td>OVHcloud</td><td>Hébergement serveurs</td><td>France</td></tr>
        <tr><td>SendGrid</td><td>E-mails transactionnels</td><td>USA (Privacy Framework)</td></tr>
        <tr><td>Cloudflare</td><td>CDN + protection DDoS</td><td>USA / mondial</td></tr>
        <tr><td>Cloudflare Web Analytics</td><td>Statistiques de visite anonymes</td><td>USA / mondial (sans cookie)</td></tr>
        <tr><td>Google Analytics</td><td>Statistiques détaillées, seulement avec ton accord</td><td>USA (Data Privacy Framework)</td></tr>
      </tbody>
    </table>
    <p>Aucun partage en dehors de cette liste. Aucune publicité programmatique. Aucun courtier en données.</p>

    <h2 id="p7"><span class="num">Section 7</span>Tes droits RGPD</h2>
    <p>Tu disposes des droits suivants, exerçables gratuitement à tout moment&nbsp;:</p>
    <ul>
      <li><b>Accès</b> — obtenir une copie complète de tes données.</li>
      <li><b>Rectification</b> — corriger les informations inexactes.</li>
      <li><b>Effacement</b> ("droit à l'oubli") — supprimer ton compte et toutes les données associées.</li>
      <li><b>Limitation</b> — geler temporairement le traitement.</li>
      <li><b>Portabilité</b> — récupérer tes données dans un format JSON standard.</li>
      <li><b>Opposition</b> — refuser certains traitements (marketing, profilage).</li>
    </ul>
    <p>Droits exercables depuis Paramètres → Vie privée ou par e-mail à <a href="mailto:privacy@lyricstream.app">privacy&#64;lyricstream.app</a>. Réponse sous 30 jours maximum.</p>

    <h2 id="p8"><span class="num">Section 8</span>Sécurité technique</h2>
    <p>Nous mettons en œuvre des mesures techniques et organisationnelles à l'état de l'art&nbsp;:</p>
    <ul>
      <li><b>Chiffrement</b> TLS 1.3 sur toutes les communications.</li>
      <li><b>Mots de passe</b> hashés avec bcrypt (12 rounds), jamais stockés en clair.</li>
      <li><b>Sauvegardes</b> chiffrées AES-256, conservées 30 jours.</li>
      <li><b>Accès interne</b> restreint par principe du moindre privilège, journalisé.</li>
      <li><b>Audits annuels</b> par un prestataire externe certifié ISO 27001.</li>
      <li><b>Notification</b> dans les 72 h en cas de violation de données.</li>
    </ul>

    <h2 id="p9"><span class="num">Section 9</span>Données des mineurs</h2>
    <p>LyricStream est accessible dès l'âge de <b>15 ans</b>. Pour les utilisateurs entre 15 et 18 ans, nous ne sollicitons aucune information autre que l'e-mail strictement nécessaire au compte. Pour les utilisateurs de moins de 15 ans, l'accord parental est requis.</p>

    <h2 id="p10"><span class="num">Section 10</span>Délégué à la protection des données</h2>
    <p>Notre DPO est joignable directement pour toute question relative à tes données personnelles&nbsp;:</p>
    <div class="callout">
      <b>Solène Vincent</b>, DPO LyricStream SAS<br/>
      <a href="mailto:dpo@lyricstream.app">dpo&#64;lyricstream.app</a> — réponse sous 5 jours ouvrés<br/>
      Courrier&nbsp;: LyricStream SAS · DPO · Port-au-Prince HT-6110, Haïti
    </div>

  </main>

  <footer class="legal-foot">
    <span>&copy; 2022–{{ year }} LyricStream SAS · Port-au-Prince</span>
    <div class="nav-pair">
      <a [routerLink]="['/', lang(), 'conditions']">← Conditions</a>
    </div>
  </footer>

</div>
